import { getInstancePathname, getInstanceUrl } from "@stores/instances/utils";
import {
  canTryProPlan,
  getProTrial,
  isBlocked,
  isCanceledOrDeleted,
} from "@stores/purchases/utils";
import { ReactElement } from "react";
import AccordionCustom from "@common/components/thirdParty/mantine/accordion/AccordionCustom";
import AccordionHeader from "@common/components/thirdParty/mantine/accordion/AccordionHeader";
import { Accordion, Flex, Text } from "@mantine/core";
import AccordionSection from "@common/components/thirdParty/mantine/accordion/AccordionSection";
import AccordionControlLink from "@common/components/thirdParty/mantine/accordion/AccordionControlLink";
import TitleWithIcon from "@common/components/thirdParty/mantine/text/TitleWithIcon";
import IconMigrate from "@common/components/icons/IconMigrate";
import { formatDate, getDaysCountBetweenDates } from "@utils/date";
import { persistUTMQueryParamsToString } from "@common/utils/router";
import { useRouter } from "next/router";
import Link from "next/link";
import IconHourglass from "@common/components/icons/IconHourglass";
import ProTrialForm from "@components/forms/trial/ProTrialForm";
import DisabledContainer from "@common/components/layout/DisabledContainer";
import { TRIAL_UP_ACTIVE, TRIAL_UP_EXPIRED } from "@stores/purchases/constants";
import { isProPlan } from "@common/utils/plan";
import { useReloadInstanceWhenChanging } from "@hooks/useReloadInstanceWhenChanging";
import { IPurchaseCardProps } from "../types";
import { getPurchaseCardId } from "../utils";
import CanceledSubscriptionSection from "../section/CanceledSubscriptionSection";
import SubscriptionSection from "../section/SubscriptionSection";
import ContactSalesSubscriptionSection from "../section/ContactSalesSubscriptionSection";
import SettingsLink from "../section/SettingsLink";
import PurchaseStatus from "../section/PurchaseStatus";

export default function EnterpriseCloudPurchaseCard({
  purchase,
  account,
  paymentMethod,
  defaultSection,
}: IPurchaseCardProps): ReactElement {
  if (!purchase.hosted_instance) {
    throw new Error("EnterpriseCloudPurchaseCard, purchase is not cloud");
  }

  const router = useRouter();

  const { id, hosted_instance, product } = purchase;
  const { plan } = product;
  const { alias_url, dns_alias } = hosted_instance!;

  const isPurchaseCanceledOrDeleted = isCanceledOrDeleted(purchase);

  const instanceUrl = getInstanceUrl({ alias_url, dns_alias });

  const { isInstanceChanging } = useReloadInstanceWhenChanging(purchase);

  const isInstanceBlocked = isBlocked(purchase) || isInstanceChanging;
  const isProTrialAllowed = canTryProPlan(purchase);
  const currentProTrialUp = getProTrial(purchase);
  const currentProTrialUpDaysOffset = currentProTrialUp
    ? getDaysCountBetweenDates(currentProTrialUp.expires_at, Date.now())
    : null;

  return (
    <DisabledContainer
      key={id}
      disabled={isPurchaseCanceledOrDeleted}
      data-testid="PurchaseCard"
    >
      <AccordionCustom
        id={getPurchaseCardId(purchase)}
        sections={[`migrate-to-cloud-${id}`]}
        defaultSection={defaultSection}
      >
        <AccordionHeader
          data-testid="PurchaseCard_header"
          title={getInstancePathname({ alias_url, dns_alias })}
          titleLink={isPurchaseCanceledOrDeleted ? undefined : instanceUrl}
          externalLink={!isPurchaseCanceledOrDeleted}
          extra={
            !isPurchaseCanceledOrDeleted && (
              <SettingsLink purchaseId={purchase.id} />
            )
          }
          progress={isInstanceChanging}
        >
          <PurchaseStatus purchase={purchase} />
        </AccordionHeader>

        {isPurchaseCanceledOrDeleted ? (
          <CanceledSubscriptionSection purchase={purchase} />
        ) : (
          <>
            <SubscriptionSection
              purchase={purchase}
              account={account}
              paymentMethod={paymentMethod}
            />

            {/* Try pro */}
            {!isProPlan(plan.alias) && (
              <>
                {isProTrialAllowed && (
                  <Accordion.Item value={`pro-trial-${id}`}>
                    <Accordion.Control disabled={isInstanceBlocked}>
                      <TitleWithIcon
                        icon={<IconHourglass size={13} />}
                        title="Try a free 14-day trial of Pro"
                        order={2}
                        size="h6"
                        isNew
                      />
                    </Accordion.Control>
                    <Accordion.Panel>
                      <ProTrialForm purchase={purchase} />
                    </Accordion.Panel>
                  </Accordion.Item>
                )}
                {currentProTrialUp?.status === TRIAL_UP_ACTIVE && (
                  <AccordionSection>
                    <TitleWithIcon
                      icon={<IconHourglass size={13} />}
                      title="Free 14-day Pro trial is in progress"
                      order={3}
                      size="h6"
                    />
                    <Flex
                      align="center"
                      direction="row"
                      justify="space-between"
                      mt="sm"
                    >
                      <Text fz="sm" m={0}>
                        Trial will end{" "}
                        {currentProTrialUpDaysOffset === 0 ? (
                          <Text span c="red.3" fw={700}>
                            today
                          </Text>
                        ) : currentProTrialUpDaysOffset === 1 ? (
                          <Text span c="red.3" fw={700}>
                            tomorrow
                          </Text>
                        ) : (
                          <>
                            on{" "}
                            <b>
                              {formatDate(currentProTrialUp.expires_at, "long")}
                            </b>
                          </>
                        )}
                      </Text>
                      <Text fz="sm" m={0}>
                        <Link
                          data-testid="trial-in-progress-upgrade-cta"
                          href={persistUTMQueryParamsToString(
                            router,
                            `/account/${id}/plan?plan=pro`,
                          )}
                        >
                          Upgrade to Pro
                        </Link>
                      </Text>
                    </Flex>
                  </AccordionSection>
                )}
                {currentProTrialUp?.status === TRIAL_UP_EXPIRED && (
                  <AccordionSection>
                    <TitleWithIcon
                      icon={<IconHourglass size={13} />}
                      title="Free 14-day Pro trial has ended"
                      order={3}
                      size="h6"
                      extraContent={
                        <Text fz="sm" m={0}>
                          <Link
                            data-testid="trial-expired-upgrade-cta"
                            href={persistUTMQueryParamsToString(
                              router,
                              `/account/${id}/plan?plan=pro`,
                            )}
                          >
                            Upgrade to Pro
                          </Link>
                        </Text>
                      }
                    />
                  </AccordionSection>
                )}
              </>
            )}

            <AccordionSection>
              <AccordionControlLink
                icon={<IconMigrate />}
                title="Migrate an existing Metabase install"
                href="https://www.metabase.com/docs/latest/cloud/migrate/guide"
                target="_blank"
                rel="noreferrer"
              />
            </AccordionSection>
          </>
        )}

        <ContactSalesSubscriptionSection />
      </AccordionCustom>
    </DisabledContainer>
  );
}
